import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Spin,
    Tooltip,
    Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { type Dayjs } from 'dayjs';
import debounce from 'lodash.debounce';
import api from '@/api/businessAdminJobs';
import Pagination from '@/components/pagination';
import ReviewList from '@/components/ReviewList';
import eyeIcon from '@/assets/images/tasker-approval-view.png';
import downloadIcon from '@/assets/images/download.png';
import { searchIcon } from '@/constants/static';
import { downloadJobInvoicePdf } from '@/utils/invoicePdf';
import { exportBusinessAdminJobs } from '@/utils/publicMethod';
import './BusinessAdminJobs.scss';

const AU_STATE_OPTIONS = [
    { value: 'NSW', label: 'NSW' },
    { value: 'VIC', label: 'VIC' },
    { value: 'QLD', label: 'QLD' },
    { value: 'SA', label: 'SA' },
    { value: 'WA', label: 'WA' },
    { value: 'TAS', label: 'TAS' },
    { value: 'ACT', label: 'ACT' },
    { value: 'NT', label: 'NT' },
];

const ACTION_ICON_SIZE = 20;

/**
 * Admin-side "Business Admin Jobs" page. Creates `tb_business_admin_job` rows stamped as
 * `source_type = MANUAL` with a pre-assigned tasker (only the external token/jobs/multipart
 * API is AUTO). Business admin and branch are dependent dropdowns (branch list reloads when
 * the business admin changes).
 */

interface ListParams {
    pageNum: number;
    pageSize: number;
    keyword: string;
    sourceType?: string;
    /** Job-status filter. Sent as the stored code; the server settles the NEW/ASSIGNED pair. */
    productStatus?: string;
    /** Assembly-date range filter (inclusive), formatted YYYY-MM-DD for the backend. */
    assemblyDateStart?: string;
    assemblyDateEnd?: string;
    /** Assigned-tasker filter. Snowflake id as a string; blank means every tasker. */
    taskerId?: string;
    sortField?: 'createTime' | 'assemblyDate';
    sortOrder?: 'asc' | 'desc';
}

interface JobRow {
    businessAdminJobId: string;
    businessAdminUserId: string;
    businessAdminDisplayName?: string;
    businessAdminBranchId: string;
    branchName?: string;
    /** The partner's own reference (Luxo sends e.g. "S123456"). Blank on admin-raised jobs. */
    jobRefId?: string;
    acceptedTaskerId?: string;
    acceptedTaskerName?: string;
    acceptedTaskerMobile?: string;
    acceptedTaskerAreaCode?: string;
    productName: string;
    productDetails?: string;
    productCategory: string;
    productStatus: string;
    assemblyDate?: string;
    assemblyCost?: number;
    /** Admin has released this future-dated job so the tasker can start before the assembly day. */
    earlyStartAllowed?: boolean;
    /** Set when the assigned tasker asked to start this future-dated job early (awaiting admin approval). */
    earlyStartRequestedTime?: string;
    sourceType?: string;
    customerName: string;
    customerNumber: string;
    customerEmail?: string;
    addressLine1?: string;
    addressLine2?: string;
    suburb?: string;
    state?: string;
    manualAttachment?: string;
    completionImages?: string[];
    completionVideo?: string;
    acceptedTime?: string;
    createTime?: string;
    /** Taskers who tapped Accept or Request on this job, awaiting admin approval. */
    pendingRequestCount?: number;
    /** Tier currently able to accept this NEW/unassigned job; null once open to all tiers. */
    currentTierName?: string;
    /** When the current tier window ends and the next tier opens (for the live countdown). */
    currentTierEndsAt?: string;
    /** True once every tier window has elapsed and the job is open to all tiers. */
    allTiersOpen?: boolean;
    /** When the job opened to all tiers (create + total tier hours); for the "unaccepted" timer. */
    allTiersOpenSince?: string;
    /** The partner's review of the tasker on this job; absent until they submit one. */
    raterRatingInfo?: any;
}

interface AcceptRequest {
    taskerId: string;
    taskerName?: string;
    areaCode?: string;
    mobile?: string;
    requestedTime?: string;
}

interface BusinessAdminOption {
    businessAdminUserId: string;
    companyName?: string;
    email?: string;
    displayName?: string;
}

interface BranchOption {
    businessAdminBranchId: string;
    branchName?: string;
    email?: string;
    mobileNumber?: string;
}

interface TaskerOption {
    taskerId: string;
    fullName?: string;
    email?: string;
    mobile?: string;
    areaCode?: string;
}

interface CreateFormValues {
    businessAdminUserId: string;
    businessAdminBranchId?: string;
    taskerId: string;
    productName: string;
    productDetails: string;
    manualAttachment?: string;
    productCategory: string;
    productStatus?: string;
    assemblyDate?: Dayjs;
    assemblyCost: string;
    customerFirstName: string;
    customerLastName: string;
    customerNumber: string;
    customerEmail?: string;
    addressLine1: string;
    addressLine2?: string;
    suburb?: string;
    state: string;
}

const productStatusOptions = [
    { value: 'NEW', label: 'Unassigned' },
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'INPROGRESS', label: 'In Progress' },
    { value: 'PAUSE', label: 'Pause' },
    { value: 'RESCHEDULE', label: 'Reschedule' },
    { value: 'SUCCESS', label: 'Complete' },
];

/**
 * The list and detail panes used to print the stored code ("NEW", "INPROGRESS"), which read as
 * jargon next to the dropdown's labels. Unknown codes fall through unchanged so a status added
 * on the backend still shows something rather than a blank cell.
 *
 * `hasTasker` settles the assigned/unassigned pair from the tasker column rather than the stored
 * status, because the two can disagree: rows written before ASSIGNED existed — and any the old
 * edit form saved with the dropdown left on "Unassigned" — still carry NEW with a tasker on them.
 * Whoever is holding the job is the truth here, so those read "Assigned" straight away instead of
 * waiting on the backfill. Statuses past assignment (In Progress, Complete, …) are left alone.
 */
const productStatusLabel = (status?: string, hasTasker?: boolean): string => {
    const code = (status || '').toUpperCase();
    if (!code) return hasTasker ? 'Assigned' : '-';
    if (code === 'NEW' && hasTasker) return 'Assigned';
    if (code === 'ASSIGNED' && !hasTasker) return 'Unassigned';
    return productStatusOptions.find(o => o.value === code)?.label || status || '-';
};

/** Status the edit form should open on, given who is actually holding the job. */
const resolveFormStatus = (status?: string, hasTasker?: boolean): string => {
    const code = (status || '').toUpperCase();
    if (!code) return hasTasker ? 'ASSIGNED' : 'NEW';
    if (code === 'NEW' && hasTasker) return 'ASSIGNED';
    if (code === 'ASSIGNED' && !hasTasker) return 'NEW';
    return code;
};

/**
 * Status filter for the list toolbar. Same labels the rows and the edit form use, so what an
 * admin filters on reads as what they see in the Status column. "Unassigned"/"Assigned" are
 * settled server-side against the tasker column, matching `productStatusLabel` — a legacy row
 * carrying NEW with a tasker on it is found under "Assigned", where the list shows it.
 */
const statusFilterOptions = [{ value: '', label: 'All statuses' }, ...productStatusOptions];

const sourceFilterOptions = [
    { value: '', label: 'All sources' },
    { value: 'MANUAL', label: 'Manual (all other sources)' },
    { value: 'AUTO', label: 'Auto (token multipart API)' },
];

const defaultFormValues: CreateFormValues = {
    businessAdminUserId: '',
    taskerId: '',
    productName: '',
    productDetails: '',
    manualAttachment: '',
    productCategory: '',
    assemblyCost: '',
    customerFirstName: '',
    customerLastName: '',
    customerNumber: '',
    customerEmail: '',
    addressLine1: '',
    addressLine2: '',
    suburb: '',
    state: 'NSW',
};

function formatAssemblyDate(value?: string) {
    if (!value) {
        return '-';
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : value;
}

function formatCreatedAt(value?: string) {
    if (!value) {
        return '-';
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : value;
}

function formatAssemblyCost(value?: number | null) {
    if (value == null || Number.isNaN(value)) {
        return '';
    }
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
}

function parseAssemblyCostInput(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
        return null;
    }
    return parsed;
}

function formatJobAddress(addressLine1?: string, addressLine2?: string, suburb?: string) {
    const parts = [addressLine1, addressLine2, suburb].filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
}

function splitCustomerName(name: string): { firstName: string; lastName: string } {
    const trimmed = name.trim();
    const spaceIndex = trimmed.indexOf(' ');
    if (spaceIndex === -1) {
        return { firstName: trimmed, lastName: '' };
    }
    return {
        firstName: trimmed.slice(0, spaceIndex),
        lastName: trimmed.slice(spaceIndex + 1).trim(),
    };
}

function buildCustomerName(firstName: string, lastName: string): string {
    return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

/** A job is invoiceable once its work is finished (status SUCCESS = "Complete"). */
function isJobCompleted(status?: string): boolean {
    return (status || '').toUpperCase() === 'SUCCESS';
}

function optionalLabel(text: string) {
    return (
        <>
            {text} <span className="field-hint">(optional)</span>
        </>
    );
}

function JobFormSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
    return (
        <section className="job-form-section">
            <header className="job-form-section-head">
                <h3>{title}</h3>
                <p>{description}</p>
            </header>
            {children}
        </section>
    );
}

function formatRemaining(ms: number): string {
    if (ms <= 0) {
        return '';
    }
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (d > 0) {
        return `${d}d ${h}h`;
    }
    if (h > 0) {
        return `${h}h ${m}m`;
    }
    if (m > 0) {
        return `${m}m ${s}s`;
    }
    return `${s}s`;
}

/**
 * "Tier & time left" cell. For a NEW/unassigned job the backend sends either the current tier +
 * when it opens to the next tier (live countdown), or `allTiersOpen`. Everything else shows "—".
 */
// The pill sits in a 2/24 column. At 15px with nowrap, "unaccepted 10d 22h" ran past the cell
// and slid under the Assign tasker control; it now sizes to the column and wraps its own label.
const tierPillBase = {
    display: 'inline-block',
    maxWidth: '100%',
    padding: '3px 8px',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.35,
    fontVariantNumeric: 'tabular-nums' as const,
    whiteSpace: 'normal' as const,
    wordBreak: 'keep-all' as const,
};

function TierCell({ row }: { row: JobRow }) {
    const needsTick = !!row.currentTierEndsAt || !!row.allTiersOpenSince;
    const [nowMs, setNowMs] = useState(Date.now());
    useEffect(() => {
        if (!needsTick) {
            return;
        }
        const id = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [needsTick]);

    const inTier = !!row.currentTierName && !!row.currentTierEndsAt;
    const remaining = row.currentTierEndsAt ? dayjs(row.currentTierEndsAt).valueOf() - nowMs : 0;

    // Still inside a tier's exclusive window → tier name + orange countdown.
    if (inTier && remaining > 0) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                <span className="tier-label" style={{ color: '#052588' }}>
                    {row.currentTierName}
                </span>
                <span style={{ ...tierPillBase, background: '#FFF2E8', color: '#D4380D' }}>
                    {formatRemaining(remaining)} left
                </span>
            </div>
        );
    }

    // Past every tier's window (backend-flagged) → open to all + ticking-up "unaccepted" timer.
    if (row.allTiersOpen) {
        const openSince = row.allTiersOpenSince ? dayjs(row.allTiersOpenSince).valueOf() : null;
        const elapsed = openSince ? nowMs - openSince : 0;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                <span className="tier-label" style={{ color: '#722ED1' }}>
                    Open to all tiers
                </span>
                {elapsed > 0 ? (
                    <span style={{ ...tierPillBase, background: '#FFF1F0', color: '#CF1322' }}>
                        unaccepted {formatRemaining(elapsed)}
                    </span>
                ) : null}
            </div>
        );
    }

    // A tier window elapsed mid-view but there are more tiers below — self-corrects on next load.
    if (inTier) {
        return <span style={{ color: '#888', fontSize: 12 }}>Tier window ended…</span>;
    }
    return <span style={{ color: '#bbb' }}>—</span>;
}

function formatTaskerDisplay(name?: string, mobile?: string, areaCode?: string) {
    if (!name && !mobile) {
        return '-';
    }
    const phone = mobile ? `${areaCode || ''}${mobile}` : '';
    if (name && phone) {
        return `${name} — ${phone}`;
    }
    return name || phone || '-';
}

function requiredLabel(text: string) {
    return (
        <>
            {text} <span className="field-required">*</span>
        </>
    );
}

interface SortableColumnHeaderProps {
    label: string;
    field: 'createTime' | 'assemblyDate';
    sortField?: 'createTime' | 'assemblyDate';
    sortOrder?: 'asc' | 'desc';
    onSort: (field: 'createTime' | 'assemblyDate') => void;
}

function SortableColumnHeader({ label, field, sortField, sortOrder, onSort }: SortableColumnHeaderProps) {
    const isSorted = sortField === field;

    return (
        <span onClick={() => onSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            {label}
            {isSorted ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
        </span>
    );
}

interface JobActionIconProps {
    src: string;
    title: string;
    onClick: () => void;
}

function JobActionIcon({ src, title, onClick }: JobActionIconProps) {
    return (
        <Tooltip title={title}>
            <img
                role="button"
                tabIndex={0}
                src={src}
                alt={title}
                style={{ width: ACTION_ICON_SIZE, height: ACTION_ICON_SIZE, cursor: 'pointer', display: 'block' }}
                onClick={event => {
                    event.stopPropagation();
                    onClick();
                }}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onClick();
                    }
                }}
            />
        </Tooltip>
    );
}

const BusinessAdminJobs = () => {
    const [loading, setLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [tableData, setTableData] = useState<JobRow[]>([]);
    const [listParams, setListParams] = useState<ListParams>({
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        sourceType: '',
        productStatus: '',
        sortField: 'createTime',
        sortOrder: 'desc',
    });

    const [form] = Form.useForm<CreateFormValues>();
    const selectedBusinessAdminId = Form.useWatch('businessAdminUserId', form);
    const [modalType, setModalType] = useState<'create' | 'edit'>('create');
    const [currentId, setCurrentId] = useState('');
    const [showFormModal, setShowFormModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailData, setDetailData] = useState<JobRow | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Job awaiting an "allow early start" confirmation; empty when the modal is closed.
    const [earlyStartJob, setEarlyStartJob] = useState<JobRow | null>(null);
    const [allowingEarlyStart, setAllowingEarlyStart] = useState(false);
    // Job whose invoice PDF is currently being generated (id), for the button spinner.
    const [invoiceJobId, setInvoiceJobId] = useState('');
    // Excel export in flight, for the Export button's spinner.
    const [exporting, setExporting] = useState(false);

    const [businessAdmins, setBusinessAdmins] = useState<BusinessAdminOption[]>([]);
    const [branches, setBranches] = useState<BranchOption[]>([]);
    const [taskers, setTaskers] = useState<TaskerOption[]>([]);
    // All active taskers (no B2B filter) for the quick-assign on unassigned AUTO jobs.
    const [allTaskers, setAllTaskers] = useState<TaskerOption[]>([]);
    const [assigningId, setAssigningId] = useState('');
    const [branchLoading, setBranchLoading] = useState(false);
    // Accept-requests modal — every acceptance now waits here, not just late requests.
    const [requestsJobId, setRequestsJobId] = useState('');
    const [requestsList, setRequestsList] = useState<AcceptRequest[]>([]);
    const [requestsLoading, setRequestsLoading] = useState(false);
    const [requestActionTaskerId, setRequestActionTaskerId] = useState('');
    /** Local buffer for the single file selected in the Upload control. Sent as
     *  `manualAttachmentFile` via multipart; if left empty, only `manualAttachment`
     *  URL (if typed) is used. */
    const [attachmentFileList, setAttachmentFileList] = useState<UploadFile[]>([]);

    const normalizeList = (responseData: any) => {
        if (Array.isArray(responseData?.records)) {
            return {
                records: responseData.records,
                total: Number(responseData.total ?? responseData.records.length ?? 0),
            };
        }
        if (Array.isArray(responseData?.list)) {
            return {
                records: responseData.list,
                total: Number(responseData.total ?? responseData.list.length ?? 0),
            };
        }
        if (Array.isArray(responseData)) {
            return {
                records: responseData,
                total: responseData.length,
            };
        }
        return { records: [], total: 0 };
    };

    const getList = async (params: ListParams) => {
        setLoading(true);
        try {
            const res = await api.getList({
                pageNum: params.pageNum,
                pageSize: params.pageSize,
                keyword: params.keyword,
                ...(params.sourceType ? { sourceType: params.sourceType } : {}),
                ...(params.productStatus ? { productStatus: params.productStatus } : {}),
                ...(params.assemblyDateStart ? { assemblyDateStart: params.assemblyDateStart } : {}),
                ...(params.assemblyDateEnd ? { assemblyDateEnd: params.assemblyDateEnd } : {}),
                ...(params.taskerId ? { taskerId: params.taskerId } : {}),
                ...(params.sortField ? { sortField: params.sortField } : {}),
                ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
            });
            const normalized = normalizeList(res.data);
            setTableData(normalized.records);
            setTotalRecord(normalized.total);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList(listParams);
    }, [listParams]);

    // Load both tasker lists once: the all-taskers one for the inline quick-assign on unassigned
    // rows, and the B2B-only one for the toolbar filter. The form loads the B2B list on open too,
    // but the filter needs it before any modal has been touched.
    useEffect(() => {
        loadAllTaskerOptions();
        loadTaskerOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedKeyword = useCallback(
        debounce((value: string) => {
            setListParams(state => ({ ...state, keyword: value, pageNum: 1 }));
        }, 500),
        []
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(event.target.value);
    };

    const handlePageChange = (pageNo: number) => {
        setListParams(state => ({ ...state, pageNum: pageNo }));
    };

    const handlePageSizeChange = (size: number) => {
        setListParams(state => ({ ...state, pageSize: size, pageNum: 1 }));
    };

    const handleAssemblyRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
        const [start, end] = range ?? [null, null];
        setListParams(state => ({
            ...state,
            assemblyDateStart: start ? start.format('YYYY-MM-DD') : undefined,
            assemblyDateEnd: end ? end.format('YYYY-MM-DD') : undefined,
            pageNum: 1,
        }));
    };

    /**
     * Options for the toolbar's tasker filter: B2B taskers only, the same roster the Add/Edit
     * form offers, so the filter lists the people who can actually hold one of these jobs rather
     * than every active tasker on the platform.
     *
     * The blank "All taskers" entry is a real option rather than a placeholder, matching the
     * source and status filters beside it: antd greys a placeholder out, so an unset filter here
     * looked disabled next to them.
     */
    const taskerFilterOptions = useMemo(
        () => [
            { value: '', label: 'All taskers' },
            ...taskers.map(tasker => ({
                value: tasker.taskerId,
                label: [tasker.fullName || 'Unnamed tasker', tasker.email].filter(Boolean).join(' · '),
            })),
        ],
        [taskers]
    );

    /**
     * Download the filtered list as a workbook. Sends the filters but not the paging, so the
     * file holds every matching job rather than the page on screen.
     */
    const handleExport = async () => {
        setExporting(true);
        try {
            await exportBusinessAdminJobs({
                keyword: listParams.keyword,
                sourceType: listParams.sourceType,
                productStatus: listParams.productStatus,
                assemblyDateStart: listParams.assemblyDateStart,
                assemblyDateEnd: listParams.assemblyDateEnd,
                taskerId: listParams.taskerId,
                sortField: listParams.sortField,
                sortOrder: listParams.sortOrder,
            });
        } catch (error: any) {
            message.error(error instanceof Error ? error.message : String(error));
        } finally {
            setExporting(false);
        }
    };

    const handleSort = (field: 'createTime' | 'assemblyDate') => {
        setListParams(state => ({
            ...state,
            pageNum: 1,
            sortField: field,
            sortOrder: state.sortField === field && state.sortOrder === 'desc' ? 'asc' : 'desc',
        }));
    };

    const loadBusinessAdminOptions = async () => {
        try {
            const res = await api.getBusinessAdminOptions();
            const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
            setBusinessAdmins(list);
        } catch (error: any) {
            message.error(error);
        }
    };

    const loadBranchOptions = async (businessAdminUserId: string) => {
        setBranchLoading(true);
        try {
            const res = await api.getBranchOptions(businessAdminUserId);
            const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
            setBranches(list);
        } catch (error: any) {
            message.error(error);
            setBranches([]);
        } finally {
            setBranchLoading(false);
        }
    };

    const loadTaskerOptions = async () => {
        try {
            const res = await api.getTaskerOptions();
            const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
            setTaskers(list);
        } catch (error: any) {
            message.error(error);
        }
    };

    // All active taskers (no B2B filter) for the quick-assign on unassigned jobs.
    const loadAllTaskerOptions = async () => {
        try {
            const res = await api.getTaskerOptions(undefined, false);
            const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
            setAllTaskers(list);
        } catch (error: any) {
            message.error(error);
        }
    };

    const handleAssignTasker = async (jobId: string, taskerId?: string) => {
        if (!taskerId) return;
        setAssigningId(jobId);
        try {
            await api.assignTasker(jobId, taskerId);
            message.success('Tasker assigned');
            getList(listParams);
        } catch (error: any) {
            message.error(error);
        } finally {
            setAssigningId('');
        }
    };

    const loadAcceptRequests = async (jobId: string) => {
        setRequestsLoading(true);
        try {
            const res = await api.getAcceptRequests(jobId);
            const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
            setRequestsList(list);
        } catch (error: any) {
            message.error(error);
            setRequestsList([]);
        } finally {
            setRequestsLoading(false);
        }
    };

    const openRequestsModal = (jobId: string) => {
        setRequestsJobId(jobId);
        setRequestsList([]);
        loadAcceptRequests(jobId);
    };

    const handleApproveRequest = async (taskerId: string) => {
        if (!requestsJobId) return;
        setRequestActionTaskerId(taskerId);
        try {
            await api.approveAcceptRequest(requestsJobId, taskerId);
            message.success('Request approved — job assigned');
            setRequestsJobId('');
            getList(listParams);
        } catch (error: any) {
            message.error(error);
            // Refresh the list in case the job was taken by someone else meanwhile.
            loadAcceptRequests(requestsJobId);
        } finally {
            setRequestActionTaskerId('');
        }
    };

    const handleDeclineRequest = async (taskerId: string) => {
        if (!requestsJobId) return;
        setRequestActionTaskerId(taskerId);
        try {
            await api.declineAcceptRequest(requestsJobId, taskerId);
            message.success('Request declined');
            const remaining = requestsList.filter(r => r.taskerId !== taskerId);
            setRequestsList(remaining);
            getList(listParams);
            if (!remaining.length) {
                setRequestsJobId('');
            }
        } catch (error: any) {
            message.error(error);
            loadAcceptRequests(requestsJobId);
        } finally {
            setRequestActionTaskerId('');
        }
    };

    const openCreateModal = async () => {
        setModalType('create');
        setCurrentId('');
        form.resetFields();
        form.setFieldsValue(defaultFormValues);
        setBranches([]);
        setAttachmentFileList([]);
        setShowFormModal(true);
        await Promise.all([loadBusinessAdminOptions(), loadTaskerOptions()]);
    };

    const openEditModal = async (row: JobRow) => {
        setModalType('edit');
        setCurrentId(row.businessAdminJobId);
        setAttachmentFileList([]);
        setShowFormModal(true);
        setDetailLoading(true);
        try {
            // Load dropdowns in parallel; we need the business admin first so the branch
            // dropdown can be populated before prefilling the form.
            await Promise.all([loadBusinessAdminOptions(), loadTaskerOptions()]);
            const res = await api.getJobDetail(row.businessAdminJobId);
            const data: JobRow = res.data?.data ?? res.data ?? row;
            if (data.businessAdminUserId) {
                await loadBranchOptions(data.businessAdminUserId);
            }
            const { firstName, lastName } = splitCustomerName(data.customerName || '');
            form.setFieldsValue({
                businessAdminUserId: data.businessAdminUserId || '',
                businessAdminBranchId: data.businessAdminBranchId || undefined,
                taskerId: data.acceptedTaskerId || '',
                productName: data.productName || '',
                productDetails: data.productDetails || '',
                manualAttachment: data.manualAttachment || '',
                productCategory: data.productCategory || '',
                // Same reconciliation the list does, so opening a legacy NEW-with-tasker row
                // does not show "Unassigned" over a filled-in tasker field. Saving would
                // correct it anyway — the API coerces NEW to ASSIGNED — but the form should
                // not state something the row contradicts.
                productStatus: resolveFormStatus(data.productStatus, !!data.acceptedTaskerId),
                assemblyDate: data.assemblyDate ? dayjs(data.assemblyDate) : undefined,
                assemblyCost:
                    data.assemblyCost == null || Number.isNaN(data.assemblyCost) ? '' : String(data.assemblyCost),
                customerFirstName: firstName,
                customerLastName: lastName,
                customerNumber: data.customerNumber || '',
                customerEmail: data.customerEmail || '',
                addressLine1: data.addressLine1 || '',
                addressLine2: data.addressLine2 || '',
                suburb: data.suburb || '',
                state: data.state || 'NSW',
            });
        } catch (error: any) {
            message.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    const openDetailModal = async (businessAdminJobId: string) => {
        setShowDetailModal(true);
        setDetailLoading(true);
        try {
            const res = await api.getJobDetail(businessAdminJobId);
            const data: JobRow = res.data?.data ?? res.data ?? null;
            setDetailData(data);
        } catch (error: any) {
            message.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    /**
     * A job dated ahead of today cannot be started by its tasker — the API refuses and the app shows
     * a disabled "Starts <date>". This offers the release only while that is actually the case, so
     * the action never appears where it would do nothing.
     */
    const canAllowEarlyStart = (item: JobRow) => {
        if (item.earlyStartAllowed) return false;
        if (!item.acceptedTaskerId) return false;
        if ((item.productStatus || '').toUpperCase() === 'SUCCESS') return false;
        if (!item.assemblyDate) return false;
        return dayjs(item.assemblyDate).startOf('day').isAfter(dayjs().startOf('day'));
    };

    const confirmAllowEarlyStart = async () => {
        if (!earlyStartJob) {
            return;
        }
        setAllowingEarlyStart(true);
        try {
            await api.allowEarlyStart(earlyStartJob.businessAdminJobId);
            message.success('Tasker can now start this job');
            setEarlyStartJob(null);
            getList(listParams);
        } catch (error: any) {
            message.error(error);
        } finally {
            setAllowingEarlyStart(false);
        }
    };

    /**
     * Build and download a Tax Invoice PDF for a completed job so the admin can
     * forward it to the partner/client. Generated in-browser from the job's own
     * fields — no wait for the 7-day batch invoice.
     */
    const handleDownloadInvoice = async (job: JobRow) => {
        setInvoiceJobId(job.businessAdminJobId);
        try {
            await downloadJobInvoicePdf(job);
            message.success('Invoice downloaded');
        } catch (error: any) {
            message.error(error?.message || 'Could not generate the invoice');
        } finally {
            setInvoiceJobId('');
        }
    };

    const confirmDelete = async () => {
        if (!currentId) {
            return;
        }
        setDeleting(true);
        try {
            await api.deleteJob(currentId);
            message.success('Job deleted');
            setShowDeleteModal(false);
            setCurrentId('');
            const targetPage =
                tableData.length === 1 && listParams.pageNum > 1 ? listParams.pageNum - 1 : listParams.pageNum;
            setListParams(state => ({ ...state, pageNum: targetPage }));
        } catch (error: any) {
            message.error(error);
        } finally {
            setDeleting(false);
        }
    };

    const handleBusinessAdminChange = (businessAdminUserId: string) => {
        form.setFieldValue('businessAdminBranchId', '');
        if (businessAdminUserId) {
            loadBranchOptions(businessAdminUserId);
        } else {
            setBranches([]);
        }
    };

    const submitForm = async () => {
        try {
            const fieldNames =
                modalType === 'create'
                    ? [
                          'businessAdminUserId',
                          'businessAdminBranchId',
                          'taskerId',
                          'assemblyDate',
                          'assemblyCost',
                          'productName',
                          'productCategory',
                          'productDetails',
                          'customerFirstName',
                          'customerLastName',
                          'customerNumber',
                          'customerEmail',
                          'addressLine1',
                          'addressLine2',
                          'suburb',
                          'state',
                          'manualAttachment',
                      ]
                    : undefined;
            const values = await form.validateFields(fieldNames);
            const assemblyCost = parseAssemblyCostInput(values.assemblyCost);
            if (assemblyCost == null || assemblyCost < 0) {
                message.error('Enter a valid assembly price (zero or greater).');
                return;
            }
            if (!values.assemblyDate) {
                message.error('Select assembly date');
                return;
            }
            setSubmitting(true);
            const payload = {
                businessAdminUserId: values.businessAdminUserId,
                businessAdminBranchId: values.businessAdminBranchId || undefined,
                taskerId: values.taskerId,
                productName: values.productName,
                productDetails: values.productDetails,
                manualAttachment: values.manualAttachment || undefined,
                productCategory: values.productCategory,
                productStatus: modalType === 'create' ? 'ASSIGNED' : values.productStatus || 'NEW',
                assemblyDate: values.assemblyDate.format('YYYY-MM-DD'),
                assemblyCost,
                customerName: buildCustomerName(values.customerFirstName, values.customerLastName),
                customerNumber: values.customerNumber,
                customerEmail: values.customerEmail || undefined,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2 || undefined,
                suburb: values.suburb?.trim() || undefined,
                state: values.state,
            };
            const formData = new FormData();
            formData.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            if (attachmentFileList.length && attachmentFileList[0].originFileObj) {
                formData.append('manualAttachmentFile', attachmentFileList[0].originFileObj);
            }
            if (modalType === 'create') {
                await api.createJob(formData);
                message.success('Job created');
            } else {
                await api.updateJob(currentId, formData);
                message.success('Job updated');
            }
            setShowFormModal(false);
            form.resetFields();
            setAttachmentFileList([]);
            setListParams(state => ({ ...state, pageNum: modalType === 'create' ? 1 : state.pageNum }));
        } catch (error: unknown) {
            const errorFields =
                error &&
                typeof error === 'object' &&
                'errorFields' in error &&
                (error as { errorFields?: unknown }).errorFields;
            if (!errorFields) {
                message.error(error instanceof Error ? error.message : String(error));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="page-main account">
                <div className="page-title">B2B Partner Tasks</div>
                <div className="select-bar">
                    <div
                        className="select-bar-left"
                        style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                        <Input
                            className="search-input"
                            placeholder="Search by job id / ref / customer / address / product / category"
                            onChange={handleSearch}
                            prefix={searchIcon}
                            style={{ width: 320 }}
                        />
                        <Select
                            value={listParams.sourceType || ''}
                            style={{ width: 220 }}
                            options={sourceFilterOptions}
                            onChange={value => setListParams(state => ({ ...state, sourceType: value, pageNum: 1 }))}
                        />
                        <Select
                            value={listParams.productStatus || ''}
                            style={{ width: 170 }}
                            options={statusFilterOptions}
                            onChange={value => setListParams(state => ({ ...state, productStatus: value, pageNum: 1 }))}
                        />
                        <DatePicker.RangePicker
                            style={{ height: 46 }}
                            allowEmpty={[true, true]}
                            placeholder={['Assembly from', 'Assembly to']}
                            value={
                                listParams.assemblyDateStart || listParams.assemblyDateEnd
                                    ? [
                                          listParams.assemblyDateStart ? dayjs(listParams.assemblyDateStart) : null,
                                          listParams.assemblyDateEnd ? dayjs(listParams.assemblyDateEnd) : null,
                                      ]
                                    : null
                            }
                            onChange={handleAssemblyRangeChange}
                        />
                        <Select
                            showSearch
                            value={listParams.taskerId || ''}
                            style={{ width: 240 }}
                            optionFilterProp="label"
                            options={taskerFilterOptions}
                            onChange={value =>
                                setListParams(state => ({ ...state, taskerId: value || undefined, pageNum: 1 }))
                            }
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Button
                            size="small"
                            style={{ height: 46, width: 180, borderRadius: 8 }}
                            loading={exporting}
                            onClick={handleExport}
                        >
                            Export
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            style={{ height: 46, width: 180, borderRadius: 8 }}
                            onClick={openCreateModal}
                        >
                            Add Job
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="table business-admin-jobs-table">
                        {totalRecord ? (
                            <>
                                <div className="table-th">
                                    <Row>
                                        <Col span={2}>Job ID</Col>
                                        <Col span={3}>Customer</Col>
                                        <Col span={2}>Address</Col>
                                        <Col span={2}>Category</Col>
                                        <Col span={2}>Product</Col>
                                        <Col span={2}>
                                            <SortableColumnHeader
                                                label="Created at"
                                                field="createTime"
                                                sortField={listParams.sortField}
                                                sortOrder={listParams.sortOrder}
                                                onSort={handleSort}
                                            />
                                        </Col>
                                        <Col span={2}>
                                            <SortableColumnHeader
                                                label="Assembly date"
                                                field="assemblyDate"
                                                sortField={listParams.sortField}
                                                sortOrder={listParams.sortOrder}
                                                onSort={handleSort}
                                            />
                                        </Col>
                                        <Col span={2}>Tier / time left</Col>
                                        <Col span={3}>Assigned tasker</Col>
                                        <Col span={2}>Status</Col>
                                        <Col span={2}>Actions</Col>
                                    </Row>
                                </div>
                                {tableData.map(item => (
                                    <Row className="table-tr" key={item.businessAdminJobId}>
                                        {/*
                                            Partners reconcile payments against their own reference
                                            (Luxo's "S123456"), so that is the ID on show. Our
                                            snowflake sits underneath — still needed for support
                                            and for jobs raised by hand here, which have no ref.
                                        */}
                                        <Col span={2} className="cell-job-id">
                                            <div className="cell-primary">
                                                {item.jobRefId || item.businessAdminJobId}
                                            </div>
                                            {item.jobRefId ? (
                                                <div className="cell-secondary">{item.businessAdminJobId}</div>
                                            ) : null}
                                        </Col>
                                        <Col span={3} className="cell-customer">
                                            <div className="cell-primary" title={item.customerName || ''}>
                                                {item.customerName || '-'}
                                            </div>
                                            {item.customerNumber ? (
                                                <div className="cell-secondary">{item.customerNumber}</div>
                                            ) : null}
                                            {item.customerEmail ? (
                                                <div className="cell-secondary" title={item.customerEmail}>
                                                    {item.customerEmail}
                                                </div>
                                            ) : null}
                                        </Col>
                                        <Col span={2} className="cell-address">
                                            <span
                                                title={formatJobAddress(
                                                    item.addressLine1,
                                                    item.addressLine2,
                                                    item.suburb
                                                )}
                                            >
                                                {formatJobAddress(item.addressLine1, item.addressLine2, item.suburb)}
                                            </span>
                                        </Col>
                                        <Col span={2}>{item.productCategory || '-'}</Col>
                                        <Col span={2} className="cell-product">
                                            <div className="cell-primary" title={item.productName}>
                                                {item.productName}
                                            </div>
                                        </Col>
                                        <Col span={2}>{formatCreatedAt(item.createTime)}</Col>
                                        <Col span={2}>{formatAssemblyDate(item.assemblyDate)}</Col>
                                        <Col span={2}>
                                            <TierCell row={item} />
                                        </Col>
                                        <Col span={3}>
                                            {item.acceptedTaskerName ? (
                                                formatTaskerDisplay(
                                                    item.acceptedTaskerName,
                                                    item.acceptedTaskerMobile,
                                                    item.acceptedTaskerAreaCode
                                                )
                                            ) : (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 4,
                                                    }}
                                                >
                                                    <Select
                                                        className="assign-tasker-select"
                                                        size="small"
                                                        showSearch
                                                        style={{ width: '100%' }}
                                                        placeholder="Assign tasker"
                                                        optionFilterProp="label"
                                                        loading={assigningId === item.businessAdminJobId}
                                                        value={undefined}
                                                        onChange={value =>
                                                            handleAssignTasker(item.businessAdminJobId, value)
                                                        }
                                                        options={allTaskers.map(t => ({
                                                            value: t.taskerId,
                                                            label: formatTaskerDisplay(
                                                                t.fullName || t.email || t.taskerId,
                                                                t.mobile,
                                                                t.areaCode
                                                            ),
                                                        }))}
                                                    />
                                                    {item.pendingRequestCount ? (
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            style={{
                                                                padding: 0,
                                                                height: 'auto',
                                                                textAlign: 'left',
                                                                fontWeight: 600,
                                                            }}
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                openRequestsModal(item.businessAdminJobId);
                                                            }}
                                                        >
                                                            {`Review ${item.pendingRequestCount} request${
                                                                item.pendingRequestCount > 1 ? 's' : ''
                                                            } →`}
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            )}
                                        </Col>
                                        <Col span={2}>
                                            <div>{productStatusLabel(item.productStatus, !!item.acceptedTaskerId)}</div>
                                            {item.earlyStartRequestedTime && !item.earlyStartAllowed ? (
                                                <span
                                                    title="The assigned tasker asked to start this job before its assembly day. Use ▶ Allow early start to approve."
                                                    style={{
                                                        display: 'inline-block',
                                                        marginTop: 4,
                                                        padding: '2px 6px',
                                                        borderRadius: 6,
                                                        background: '#FFF7E6',
                                                        color: '#D46B08',
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    Early start requested
                                                </span>
                                            ) : null}
                                        </Col>
                                        <Col span={2}>
                                            <div className="approval-actions-toolbar">
                                                <JobActionIcon
                                                    src={eyeIcon}
                                                    title="Details"
                                                    onClick={() => openDetailModal(item.businessAdminJobId)}
                                                />
                                                <JobActionIcon
                                                    src={require('@/assets/images/edit-icon.png')}
                                                    title="Edit"
                                                    onClick={() => openEditModal(item)}
                                                />
                                                {isJobCompleted(item.productStatus) ? (
                                                    <JobActionIcon
                                                        src={downloadIcon}
                                                        title={
                                                            invoiceJobId === item.businessAdminJobId
                                                                ? 'Preparing invoice…'
                                                                : 'Download invoice (PDF)'
                                                        }
                                                        onClick={() => handleDownloadInvoice(item)}
                                                    />
                                                ) : null}
                                                {canAllowEarlyStart(item) ? (
                                                    <JobActionIcon
                                                        src={require('@/assets/images/play.png')}
                                                        title={
                                                            item.earlyStartRequestedTime
                                                                ? 'Approve early-start request'
                                                                : 'Allow tasker to start early'
                                                        }
                                                        onClick={() => setEarlyStartJob(item)}
                                                    />
                                                ) : null}
                                                <JobActionIcon
                                                    src={require('@/assets/images/delete-icon.png')}
                                                    title="Delete"
                                                    onClick={() => {
                                                        setCurrentId(item.businessAdminJobId);
                                                        setShowDeleteModal(true);
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                ))}
                                <Pagination
                                    total={totalRecord}
                                    pageChange={handlePageChange}
                                    pageNo={listParams.pageNum}
                                    pageSize={listParams.pageSize}
                                    onPageSizeChange={handlePageSizeChange}
                                    pageSizeOptions={[10, 20, 50, 100]}
                                />
                            </>
                        ) : (
                            <div className="no-data flex-center">No data</div>
                        )}
                    </div>
                )}
            </div>

            <Modal
                width={860}
                centered
                maskClosable={false}
                className="business-admin-job-form-modal"
                title={
                    <div className="job-form-modal-title">
                        <div>{modalType === 'create' ? 'Add Job' : 'Edit Job'}</div>
                        <p className="job-form-modal-subtitle">
                            Fill in the customer + product details. Required fields are marked with *.
                        </p>
                    </div>
                }
                open={showFormModal}
                onCancel={() => setShowFormModal(false)}
                footer={null}
            >
                <Spin spinning={submitting || detailLoading}>
                    <Form
                        form={form}
                        layout="vertical"
                        className="add-Categorie-form business-admin-job-form"
                        scrollToFirstError
                    >
                        <div className="job-form-scroll">
                            <div className="job-form-layout">
                                <JobFormSection
                                    title="Assignment"
                                    description="Choose the business admin, branch, and tasker for this job."
                                >
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="businessAdminUserId"
                                                label={requiredLabel('Business admin')}
                                                rules={[{ required: true, message: 'Select a business admin' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    placeholder="Select business admin"
                                                    optionFilterProp="label"
                                                    onChange={handleBusinessAdminChange}
                                                    options={businessAdmins.map(b => ({
                                                        value: b.businessAdminUserId,
                                                        label:
                                                            b.displayName ||
                                                            b.companyName ||
                                                            b.email ||
                                                            b.businessAdminUserId,
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="businessAdminBranchId" label={optionalLabel('Branch')}>
                                                <Select
                                                    allowClear
                                                    placeholder="No branch"
                                                    loading={branchLoading}
                                                    disabled={!selectedBusinessAdminId}
                                                    options={branches.map(b => ({
                                                        value: b.businessAdminBranchId,
                                                        label: b.branchName || b.email || b.businessAdminBranchId,
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="taskerId"
                                                label={requiredLabel('Assigned tasker')}
                                                rules={[{ required: true, message: 'Select a tasker' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    placeholder="Select tasker"
                                                    optionFilterProp="label"
                                                    options={taskers.map(t => ({
                                                        value: t.taskerId,
                                                        label: formatTaskerDisplay(
                                                            t.fullName || t.email || t.taskerId,
                                                            t.mobile,
                                                            t.areaCode
                                                        ),
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </JobFormSection>

                                <JobFormSection
                                    title="Job details"
                                    description="Capture what needs to be done and current status."
                                >
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="productName"
                                                label={requiredLabel('Product Name')}
                                                rules={[{ required: true, message: 'Enter product name' }]}
                                            >
                                                <Input placeholder="e.g. Smart TV install" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="productCategory"
                                                label={requiredLabel('Product Category')}
                                                rules={[{ required: true, message: 'Enter product category' }]}
                                            >
                                                <Input placeholder="e.g. Electronics" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item
                                                name="productDetails"
                                                label={requiredLabel('Product Details')}
                                                rules={[{ required: true, message: 'Enter product details' }]}
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="Describe the job, model number, accessories, etc."
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {modalType === 'edit' ? (
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="productStatus"
                                                    label={requiredLabel('Job Status')}
                                                    rules={[{ required: true, message: 'Select status' }]}
                                                >
                                                    <Select options={productStatusOptions} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ) : null}
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="assemblyDate"
                                                label={requiredLabel('Assembly date')}
                                                rules={[
                                                    {
                                                        validator: (_, value?: Dayjs) => {
                                                            if (!value) {
                                                                return Promise.reject(
                                                                    new Error('Select assembly date')
                                                                );
                                                            }
                                                            if (value.startOf('day').isBefore(dayjs().startOf('day'))) {
                                                                return Promise.reject(
                                                                    new Error('Assembly date cannot be in the past')
                                                                );
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    format="DD MMM YYYY"
                                                    style={{ width: '100%' }}
                                                    disabledDate={current =>
                                                        !!current && current < dayjs().startOf('day')
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="assemblyCost"
                                                label={requiredLabel('Assembly price')}
                                                rules={[{ required: true, message: 'Enter assembly price' }]}
                                            >
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    step={0.01}
                                                    inputMode="decimal"
                                                    placeholder="0.00"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </JobFormSection>

                                <JobFormSection
                                    title="Customer details"
                                    description="Who requested this job and how to contact them."
                                >
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="customerFirstName"
                                                label={requiredLabel('First Name')}
                                                rules={[{ required: true, message: 'Enter first name' }]}
                                            >
                                                <Input placeholder="First name" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="customerLastName"
                                                label={requiredLabel('Last Name')}
                                                rules={[{ required: true, message: 'Enter last name' }]}
                                            >
                                                <Input placeholder="Last name" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="customerNumber"
                                                label={requiredLabel('Customer Number')}
                                                rules={[{ required: true, message: 'Enter customer number' }]}
                                                extra={
                                                    <span className="customer-number-hint">
                                                        Australian mobile (0412 345 678) or landline (02 1234 5678)
                                                    </span>
                                                }
                                            >
                                                <Input type="tel" inputMode="tel" placeholder="0412 345 678" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="customerEmail"
                                                label={requiredLabel('Customer Email')}
                                                rules={[
                                                    { required: true, message: 'Enter customer email' },
                                                    { type: 'email', message: 'Enter a valid email' },
                                                ]}
                                            >
                                                <Input type="email" placeholder="name@example.com" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </JobFormSection>

                                <JobFormSection
                                    title="Location & attachments"
                                    description="Address and any manuals or completion evidence."
                                >
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="addressLine1"
                                                label={requiredLabel('Address Line 1')}
                                                rules={[{ required: true, message: 'Enter address line 1' }]}
                                            >
                                                <Input placeholder="Address line 1" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="addressLine2" label={optionalLabel('Address Line 2')}>
                                                <Input placeholder="Address line 2" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="suburb" label={optionalLabel('Suburb')}>
                                                <Input placeholder="Suburb" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="state"
                                                label={requiredLabel('State / Territory')}
                                                rules={[{ required: true, message: 'Select state / territory' }]}
                                            >
                                                <Select placeholder="Select state" options={AU_STATE_OPTIONS} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item name="manualAttachment" label={optionalLabel('Attachment URL')}>
                                                <Input placeholder="https://..." />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item
                                                className="job-form-attachment-file"
                                                label={optionalLabel('Attachment File')}
                                                extra="Upload a PDF/JPEG/PNG/WEBP. Overrides the URL above when present."
                                            >
                                                <Upload
                                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                                    maxCount={1}
                                                    beforeUpload={() => false}
                                                    fileList={attachmentFileList}
                                                    onChange={({ fileList }) => setAttachmentFileList(fileList)}
                                                >
                                                    <Button>Choose File</Button>
                                                </Upload>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </JobFormSection>
                            </div>
                        </div>

                        <Row gutter={16} className="job-form-footer">
                            <Col span={12}>
                                <Button className="submit-btn cancel-btn" onClick={() => setShowFormModal(false)}>
                                    Close
                                </Button>
                            </Col>
                            <Col span={12}>
                                <Button type="primary" className="submit-btn" onClick={submitForm}>
                                    {submitting ? 'Saving…' : modalType === 'create' ? 'Create Job' : 'Update Job'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Modal>

            <Modal
                width={720}
                centered
                maskClosable={false}
                title="Business Admin Job Details"
                open={showDetailModal}
                onCancel={() => setShowDetailModal(false)}
                footer={null}
            >
                <Spin spinning={detailLoading}>
                    {detailData ? (
                        <Descriptions
                            bordered
                            column={1}
                            size="small"
                            className="business-admin-job-detail"
                            labelStyle={{ width: 180, fontWeight: 600, verticalAlign: 'top' }}
                            contentStyle={{ verticalAlign: 'top' }}
                        >
                            <Descriptions.Item label="Partner job reference">
                                {detailData.jobRefId || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Faastaa job ID">
                                {detailData.businessAdminJobId || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Product">{detailData.productName || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Product category">
                                {detailData.productCategory || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Product details">
                                {detailData.productDetails || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                {productStatusLabel(detailData.productStatus, !!detailData.acceptedTaskerId)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Assembly date">
                                {formatAssemblyDate(detailData.assemblyDate)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Assembly price">
                                {formatAssemblyCost(detailData.assemblyCost) || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {formatCreatedAt(detailData.createTime)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Source">{detailData.sourceType || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Business admin">
                                {detailData.businessAdminDisplayName || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Branch">{detailData.branchName || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Assigned tasker">
                                {formatTaskerDisplay(
                                    detailData.acceptedTaskerName,
                                    detailData.acceptedTaskerMobile,
                                    detailData.acceptedTaskerAreaCode
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Customer">
                                {detailData.customerName || '-'}
                                {detailData.customerNumber ? ` (${detailData.customerNumber})` : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Customer email">
                                {detailData.customerEmail || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Address">
                                {formatJobAddress(detailData.addressLine1, detailData.addressLine2, detailData.suburb)}
                            </Descriptions.Item>
                            <Descriptions.Item label="State / Territory">{detailData.state || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Manual attachment">
                                {detailData.manualAttachment ? (
                                    <a href={detailData.manualAttachment} target="_blank" rel="noreferrer">
                                        {detailData.manualAttachment}
                                    </a>
                                ) : (
                                    '-'
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Completion evidence">
                                {detailData.completionImages && detailData.completionImages.length ? (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {detailData.completionImages.map((url, idx) => (
                                            <a key={`${url}-${idx}`} href={url} target="_blank" rel="noreferrer">
                                                <img
                                                    src={url}
                                                    alt={`Completion evidence ${idx + 1}`}
                                                    style={{
                                                        width: 72,
                                                        height: 72,
                                                        objectFit: 'cover',
                                                        borderRadius: 6,
                                                    }}
                                                />
                                            </a>
                                        ))}
                                    </div>
                                ) : detailData.completionVideo ? (
                                    <video
                                        src={detailData.completionVideo}
                                        controls
                                        style={{ width: '100%', maxWidth: 320, borderRadius: 6 }}
                                    />
                                ) : (
                                    '-'
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    ) : (
                        <div className="no-data flex-center">No data</div>
                    )}
                    {detailData && isJobCompleted(detailData.productStatus) ? (
                        <div
                            style={{
                                marginTop: 16,
                                padding: '14px 16px',
                                border: '1px solid #d6e0ff',
                                background: '#f5f8ff',
                                borderRadius: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                                flexWrap: 'wrap',
                            }}
                        >
                            <div>
                                <div style={{ fontWeight: 600, color: '#052588' }}>Invoice ready</div>
                                <div style={{ color: '#666', fontSize: 13 }}>
                                    This job is complete. Download the tax invoice PDF to send to the client.
                                </div>
                            </div>
                            <Button
                                type="primary"
                                loading={invoiceJobId === detailData.businessAdminJobId}
                                onClick={() => handleDownloadInvoice(detailData)}
                            >
                                Download invoice (PDF)
                            </Button>
                        </div>
                    ) : null}
                    {detailData?.raterRatingInfo ? (
                        <div style={{ marginTop: 16 }}>
                            <div className="card-title-large">Review</div>
                            {/* Same renderer as the tasker profile's Reviews tab. */}
                            <ReviewList tableData={[detailData.raterRatingInfo]} />
                        </div>
                    ) : null}
                </Spin>
            </Modal>

            <Modal
                width={620}
                centered
                maskClosable={false}
                title="Taskers waiting for approval"
                open={!!requestsJobId}
                onCancel={() => setRequestsJobId('')}
                footer={null}
            >
                <Spin spinning={requestsLoading}>
                    <p style={{ color: '#666', marginTop: 0 }}>
                        These taskers want to take this job. Nothing starts until you approve one. Approving assigns the
                        job and automatically rejects the rest.
                    </p>
                    {requestsList.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {requestsList.map(req => (
                                <div
                                    key={req.taskerId}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '10px 14px',
                                        border: '1px solid #eee',
                                        borderRadius: 8,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>
                                            {formatTaskerDisplay(req.taskerName, req.mobile, req.areaCode)}
                                        </div>
                                        {req.requestedTime ? (
                                            <div style={{ color: '#999', fontSize: 12 }}>
                                                Requested {dayjs(req.requestedTime).format('DD MMM YYYY, HH:mm')}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Button
                                            size="small"
                                            danger
                                            loading={requestActionTaskerId === req.taskerId}
                                            onClick={() => handleDeclineRequest(req.taskerId)}
                                        >
                                            Decline
                                        </Button>
                                        <Button
                                            size="small"
                                            type="primary"
                                            loading={requestActionTaskerId === req.taskerId}
                                            onClick={() => handleApproveRequest(req.taskerId)}
                                        >
                                            Approve
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-data flex-center">No pending requests</div>
                    )}
                </Spin>
            </Modal>

            <Modal width={560} centered maskClosable={false} open={!!earlyStartJob} closeIcon={<></>} footer={null}>
                <div className="modal-title">Allow early start</div>
                <div className="modal-tip tip-font-20">
                    {`Are you sure you want to let ${
                        earlyStartJob?.acceptedTaskerName || 'the assigned tasker'
                    } start this job before ${
                        earlyStartJob?.assemblyDate
                            ? dayjs(earlyStartJob.assemblyDate).format('DD MMM YYYY')
                            : 'the assembly date'
                    }?`}
                </div>
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button className="submit-btn" onClick={() => setEarlyStartJob(null)} disabled={allowingEarlyStart}>
                        Cancel
                    </Button>
                    <Button
                        className="submit-btn"
                        type="primary"
                        loading={allowingEarlyStart}
                        onClick={confirmAllowEarlyStart}
                    >
                        Yes, allow start
                    </Button>
                </div>
            </Modal>

            <Modal width={560} centered maskClosable={false} open={showDeleteModal} closeIcon={<></>} footer={null}>
                <div className="modal-title">Delete job</div>
                <div className="modal-tip tip-font-20">Are you sure you want to delete this job?</div>
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button className="submit-btn" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button className="submit-btn" type="primary" loading={deleting} onClick={confirmDelete}>
                        Yes, delete
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default BusinessAdminJobs;
